import React, { useState, useEffect } from 'react';

// --- Configuration ---
const ROAST_FUNCTION_URL = 'https://us-central1-roast-my-gift-32692251-3938c.cloudfunctions.net/roastAndFix';

// --- DYNAMIC CONTENT LISTS ---
const PLACEHOLDER_SETS = {
  savage: [
    "A used gym membership for my partner",
    "A vacuum cleaner for my girlfriend",
    "A DIY home haircut kit",
    "A five-pound bag of flour",
    "Matching 'His & Hers' bath towels",
    "A framed photo of myself",
    "A self-help book titled 'Stop Procrastinating'",
    "A novelty tie with cartoon characters",
    "A broken toaster I plan to 'fix later'",
    "Socks with my pet's face printed on them",
    "A calendar from last year",
    "A single zucchini from my garden",
    "A gift card to a store that closed",
    "A box of expired protein bars",
    "A motivational poster with a blurry eagle"
  ],
  snobby: [
    "A gift card to Applebee's",
    "Polyester bed sheets (600 thread count)",
    "A 'Live Laugh Love' wall sign",
    "Fake plants from a discount store",
    "A wine glass that says 'Mommy's Juice'",
    "A generic box of chocolates from the gas station",
    "Cologne that costs less than $20",
    "A replica handbag I swear looks real",
    "Furniture made of particle board",
    "A print of the Eiffel Tower from a tourist shop",
    "Instant coffee for a coffee lover",
    "A plastic chandelier",
    "A t-shirt with a 'clever' slogan",
    "Synthetically scented candles",
    "A tie-dye kit for an adult"
  ],
  helpful: [
    "A pack of white socks",
    "A fruitcake in a tin",
    "A plain beige sweater",
    "A calendar of local bridges",
    "A set of plastic food containers",
    "A book about organizing taxes",
    "A flashlight",
    "A generic mug that says 'Coffee'",
    "A heavy dictionary",
    "A subscription to a newspaper they don't read",
    "A pair of scissors",
    "A bag of cough drops",
    "A potato peeler",
    "A 1000-piece puzzle of a blue sky",
    "A gift card for $5.00"
  ]
};

const BUTTON_TEXTS = [
  "Redeem Yourself ↗",
  "Buy the Real Thing ↗",
  "Save Your Reputation ↗",
  "Fix It Now ↗"
];

const App = () => {
  const [giftIdea, setGiftIdea] = useState('');
  const [roastData, setRoastData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // --- 1. SMART INITIALIZATION (Read URL on load) ---
  const [vibe, setVibe] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const urlVibe = params.get('vibe');
      if (urlVibe && ['savage', 'snobby', 'helpful'].includes(urlVibe)) {
        return urlVibe;
      }
    }
    return 'savage'; // Default
  });
  
  // State for cycling placeholders
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  // --- 2. LIVE URL UPDATER ---
  const changeVibe = (newVibe) => {
    setVibe(newVibe);
    // Update the URL in the browser without reloading
    const newUrl = `${window.location.pathname}?vibe=${newVibe}`;
    window.history.pushState({}, '', newUrl);
  };

  // --- Dynamic UI Text based on Vibe ---
  const getUIText = () => {
    switch(vibe) {
      case 'snobby':
        return { 
          button: 'JUDGE ME', 
          verdict: '🧐 THE CRITIQUE', 
          scoreLabel: 'Tackiness Score', 
          loading: 'Consulting the design elite...',
          share: 'Share this Critique',
          placeholderList: PLACEHOLDER_SETS.snobby,
          subtitle: "Does Your Taste Meet the Standard?" // New Snobby Hook
        };
      case 'helpful':
        return { 
          button: 'HELP ME', 
          verdict: '💝 THE ADVICE', 
          scoreLabel: 'Bad Gift Score', 
          loading: 'Finding thoughtful alternatives...',
          share: 'Share this Advice',
          placeholderList: PLACEHOLDER_SETS.helpful,
          subtitle: "Let's Find the Perfect Gift." // New Helpful Hook
        };
      default: // Savage (Default)
        return { 
          button: 'ROAST ME', 
          verdict: '💀 THE VERDICT', 
          scoreLabel: 'Cringe Level', 
          loading: 'Preparing emotional damage...',
          share: 'Share this L',
          placeholderList: PLACEHOLDER_SETS.savage,
          subtitle: "Is Your Gift a 'Win' or a 'Fail'?" // New Savage Hook
        };
    }
  };
  const ui = getUIText();

  // Reset placeholder index when vibe changes
  useEffect(() => {
    setPlaceholderIndex(0);
  }, [vibe]);

  // Rotate placeholders
  useEffect(() => {
    const intervalId = setInterval(() => {
      setPlaceholderIndex(prevIndex => (prevIndex + 1) % ui.placeholderList.length);
    }, 4000); 
    return () => clearInterval(intervalId);
  }, [ui.placeholderList]);

  const handleAutocomplete = () => {
    setGiftIdea(ui.placeholderList[placeholderIndex]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Tab' || e.keyCode === 9) {
      e.preventDefault(); 
      handleAutocomplete();
    }
  };

  const handleRoast = async (e) => {
    e.preventDefault();
    const idea = giftIdea.trim() || ui.placeholderList[placeholderIndex]; 
    
    if (!idea) {
      setError("Please enter a gift idea.");
      return; 
    }

    setLoading(true);
    setRoastData(null);
    setError(null);

    try {
      const response = await fetch(ROAST_FUNCTION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: idea, mode: vibe })
      });

      if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error.message || "The server returned an error.");
      }
      
      const result = await response.json();
      setRoastData(result);
      setGiftIdea(''); 

    } catch (e) {
      const errorMessage = e.message || "Failed to get a response. Is the server running?";
      setError(`SYSTEM ERROR: ${errorMessage}`);
      console.error("API Call Error:", e);
    } finally {
      setLoading(false);
    }
  };

  // --- Sub-Components ---
  const FixerCard = ({ title, description, emoji }) => {
    const searchUrl = `https://www.amazon.com/s?k=${encodeURIComponent(title)}&tag=roastmygift07-20`;
    const [btnText, setBtnText] = useState("Find on Amazon ↗");
    useEffect(() => {
       setBtnText(BUTTON_TEXTS[Math.floor(Math.random() * BUTTON_TEXTS.length)]);
    }, []);

    return (
      <a 
        href={searchUrl} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="flex flex-col h-full p-6 bg-gray-800 rounded-xl border border-gray-700 hover:border-cyan-500 transition duration-300 shadow-lg group relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 p-4 opacity-20 text-6xl pointer-events-none group-hover:scale-110 transition duration-500">
           {emoji || '✨'}
        </div>
        <h3 className="text-xl font-bold text-cyan-400 mb-2 group-hover:text-cyan-300 z-10 pr-8">
          {emoji} {title}
        </h3>
        <p className="text-gray-400 text-sm flex-grow mb-4 z-10">
          {description}
        </p>
        <div className="mt-auto z-10">
          <span className="inline-block w-full text-center py-2 rounded-lg bg-gray-700 text-white font-bold text-sm group-hover:bg-cyan-600 transition">
            {btnText}
          </span>
        </div>
      </a>
    );
  };
  
  // --- UPDATED ROAST CARD WITH READ MORE FEATURE ---
  const RoastCard = ({ data }) => {
    const fixers = data.fixers || [];
    const score = data.score || 5;
    const [isExpanded, setIsExpanded] = useState(false); // ADDED: Toggle State

    const handleShare = () => {
      const baseUrl = window.location.origin;
      const smartLink = `${baseUrl}/?vibe=${vibe}`;
      const text = `My gift idea got roasted: "${data.roast.substring(0, 50)}..." 💀\n\nCheck yours at: ${smartLink}`;
      navigator.clipboard.writeText(text);
      alert("Copied to clipboard! (Link includes your current Vibe)");
    };

    return (
      <div className="w-full max-w-6xl mx-auto mt-12 grid grid-cols-1 gap-12 text-left animate-fade-in-up">
        
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
             <h2 className="text-3xl font-extrabold text-red-500 drop-shadow-[0_0_8px_rgba(255,0,0,0.5)]">
               {ui.verdict}
             </h2>
             <div className="flex items-center space-x-2">
                <span className="text-gray-400 uppercase text-xs font-bold tracking-widest">{ui.scoreLabel}</span>
                <span className="bg-red-600 text-white px-3 py-1 rounded font-black text-lg shadow-[0_0_10px_rgba(220,38,38,0.8)] rotate-2">
                   {score}/10
                </span>
             </div>
          </div>

          <div className="bg-gray-800/80 p-8 rounded-2xl border border-red-900/50 shadow-2xl backdrop-blur-sm relative">
            
            {/* TRUNCATED TEXT LOGIC */}
            <div className="relative">
              <p className={`text-gray-100 text-xl italic leading-relaxed font-serif transition-all duration-300 ${isExpanded ? '' : 'line-clamp-3'}`}>
                "{data.roast}"
              </p>
              
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-3 text-xs font-bold text-cyan-400 hover:text-cyan-300 uppercase tracking-widest flex items-center gap-1 transition-colors"
              >
                {isExpanded ? (
                  <><span>↑ Show Less</span></>
                ) : (
                  <><span>↓ Read Full Critique</span></>
                )}
              </button>
            </div>

            <button 
              onClick={handleShare}
              className="mt-6 text-sm font-bold text-gray-500 hover:text-white flex items-center space-x-2 transition-colors border-t border-gray-700/50 pt-4 w-full"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path></svg>
              <span>{ui.share}</span>
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <h2 className="text-3xl font-extrabold text-cyan-400 drop-shadow-[0_0_8px_rgba(0,255,255,0.5)]">
            ✨ THE SAVE
          </h2>
          <p className="text-xs text-gray-500 italic">As an Amazon Associate I earn from qualifying purchases.</p>
          <p className="text-gray-400">Here is what you <i>should</i> buy instead:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
             {fixers.map((fix, index) => (
                <FixerCard 
                  key={index} 
                  title={fix.title} 
                  description={fix.description} 
                  emoji={fix.emoji}
                />
             ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col items-center p-4 sm:p-8 font-sans">
      
      <div className="w-full max-w-4xl text-center">
        <h1 
          className="text-6xl font-extrabold mt-10 mb-2 tracking-tighter"
          style={{ 
            backgroundImage: 'linear-gradient(90deg, #00FFFF, #FF00FF)', 
            WebkitBackgroundClip: 'text', 
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 10px rgba(0, 255, 255, 0.5), 0 0 10px rgba(255, 0, 255, 0.5)' 
          }}
        >
          ROAST MY GIFT
        </h1>
        {/* Dynamic Subtitle - Bold & Bright */}
        <p className="text-2xl md:text-3xl text-gray-200 font-medium tracking-tight transition-all duration-300">
          {ui.subtitle}
        </p>
        <p className="text-lg text-gray-400 mb-10">
          Let our AI judge you, then fix it.
        </p>

        <form onSubmit={handleRoast} className="p-6 rounded-xl mb-12">
          
          {/* VIBE SELECTOR DROPDOWN */}
          <div className="mb-4 flex justify-center">
            <div className="inline-flex bg-gray-800 rounded-lg p-1 border border-gray-700">
               <button type="button" onClick={() => changeVibe('savage')} className={`px-4 py-2 rounded-md text-sm font-bold transition ${vibe === 'savage' ? 'bg-red-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}>🔥 Savage</button>
               <button type="button" onClick={() => changeVibe('snobby')} className={`px-4 py-2 rounded-md text-sm font-bold transition ${vibe === 'snobby' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}>🧐 Snobby</button>
               <button type="button" onClick={() => changeVibe('helpful')} className={`px-4 py-2 rounded-md text-sm font-bold transition ${vibe === 'helpful' ? 'bg-green-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}>💖 Helpful</button>
            </div>
          </div>

          <div className="flex flex-col relative">
            <input
              id="gift-input"
              type="text"
              value={giftIdea}
              onChange={(e) => setGiftIdea(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={ui.placeholderList[placeholderIndex]}
              className="w-full p-4 pr-32 bg-gray-800 text-white rounded-xl shadow-inner border border-fuchsia-500 focus:ring-cyan-500 focus:border-cyan-500 transition duration-300 placeholder-gray-500 text-xl"
              disabled={loading}
            />
            <div 
                className="absolute text-xs text-gray-500 italic flex items-center"
                style={{ bottom: '0.5rem', right: '1rem' }} 
            > 
                <span className="hidden sm:inline">(Tab to auto-complete)</span>
                <button 
                  type="button" 
                  onClick={handleAutocomplete}
                  className="sm:hidden ml-2 px-2 py-1 text-xs font-bold rounded-md bg-cyan-500 text-gray-900"
                >
                  Auto
                </button>
            </div>
          </div>
          <button
            type="submit"
            className={`w-full px-8 py-4 text-xl font-bold rounded-xl shadow-lg 
                      text-white transition duration-300 ease-in-out 
                      disabled:opacity-50 flex items-center justify-center space-x-2 mt-4
                      ${vibe === 'savage' ? 'bg-gradient-to-r from-red-500 to-fuchsia-600 hover:shadow-[0_0_15px_rgba(255,0,0,0.5)]' : ''}
                      ${vibe === 'snobby' ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-[0_0_15px_rgba(120,0,255,0.5)]' : ''}
                      ${vibe === 'helpful' ? 'bg-gradient-to-r from-green-500 to-teal-500 hover:shadow-[0_0_15px_rgba(0,255,100,0.5)]' : ''}
                      `} 
            disabled={loading}
          >
            {vibe === 'savage' && <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 18.657A8 8 0 0113.617 21c-4.418 0-8-3.582-8-8a8 8 0 0113.111-6.111zM12 3v2m0 14v2m-5.464-16.12l1.414 1.414M16.12 7.879l1.414-1.414M7.879 16.12l-1.414 1.414M16.12 16.12l1.414 1.414M21 12h-2m-17 0H3m14.657-3.657l-1.414-1.414M9.343 14.657l-1.414 1.414M12 9v6m3-3H9"></path></svg>}
            {vibe === 'snobby' && <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>}
            {vibe === 'helpful' && <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>}
            
            <span>{loading ? 'ANALYZING...' : ui.button}</span>
          </button>
        </form>

        {loading && (
          <div className="flex items-center justify-center p-10 bg-gray-900 rounded-xl shadow-2xl max-w-3xl mx-auto border border-cyan-500">
            <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-cyan-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-lg text-gray-300">{ui.loading}</span>
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-900/50 border-l-4 border-red-500 text-red-300 rounded max-w-4xl mx-auto">
            <p>{error}</p>
          </div>
        )}

        {roastData && <RoastCard data={roastData} />}

      </div>
    </div>
  );
};

export default App;